/**
 * Spaces — the PMS room map on the listing photos, joined to the spaces rows
 * (toolkit bedroom-showcase "photos from the PMS room map", 9/20; port of
 * includes/BedroomShowcase/RoomPhotos.php). Pure: no React, no DOM.
 *
 * Central puts the PMS room on each listing photo (central#3703, live since
 * 10/12): `room_uid` groups the photos, `room_name` is the operator's room
 * name and `room_position` its 0-based position in the PMS room list. The
 * public property-spaces rows carry NO uid (id / name / number only; verified
 * against feed 326 amber-sunset 2026-10-18), so the join cannot be uid to
 * uid. It goes:
 *
 *  1. Photos group by `room_uid` (never by name: names repeat within a
 *     listing, "Upper Level - hall bath" x2 on Lakefront).
 *  2. A room joins a space by POSITION: the space's synced `number` plus a
 *     fixed offset equals the room's `room_position` AND the two names agree
 *     (normalized). Numbers are 1-based and positions 0-based, so the offset
 *     is usually -1; it is detected over {-1, 0} as the one that matches the
 *     most spaces (a tie trusts neither), the way the toolkit does, because
 *     some PMS feeds number from 0.
 *  3. A space still unmatched joins by NAME, only when exactly one space and
 *     exactly one room carry that name: two rooms called "Bedroom" identify
 *     neither (a wrong room's photos are worse than none).
 *
 * A room is handed to at most one space; a space with no number or a blank
 * name can only match by name.
 */
import { normalizeRoomText, type RoomPhoto } from '../gallery/rooms'

/** A listing photo as the Gallery's key holds it; only these fields are read. */
export type RoomMapPhoto = RoomPhoto & { url: string; caption?: string | null }

/** The two space fields the join reads. */
export type RoomMapSpace = { name: string | null | undefined; number: number | null | undefined }

export type RoomMapRoom = {
  /** The room's normalized name ('' when the photos carry none). */
  name: string
  /** The room's 0-based PMS position; null when the photos carry none. */
  position: number | null
  photos: { url: string; caption: string }[]
}

/** The offsets tried between a space's number and a room's position. */
const OFFSETS = [-1, 0] as const

const scalar = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value).trim() : ''

/** Photos with a room, grouped by uid in first-photo order; gallery order inside each room. */
export function roomsFromPhotos(photos: ReadonlyArray<RoomMapPhoto | null | undefined>): Map<string, RoomMapRoom> {
  const rooms = new Map<string, RoomMapRoom>()
  for (const photo of photos) {
    if (!photo || typeof photo !== 'object') continue
    const uid = scalar(photo.room_uid)
    const url = typeof photo.url === 'string' ? photo.url : ''
    if (uid === '' || url === '') continue
    let room = rooms.get(uid)
    if (!room) {
      const position = photo.room_position
      room = {
        name: normalizeRoomText(scalar(photo.room_name)),
        position: typeof position === 'number' && Number.isFinite(position) ? Math.trunc(position) : null,
        photos: [],
      }
      rooms.set(uid, room)
    }
    room.photos.push({ url, caption: typeof photo.caption === 'string' ? photo.caption : '' })
  }
  return rooms
}

/** The one room at `number + offset` whose name is `name`; null when none or several. */
function roomAt(
  space: RoomMapSpace,
  name: string,
  offset: number,
  byPosition: Map<number, string[]>,
  rooms: Map<string, RoomMapRoom>,
): string | null {
  const number = space.number
  if (name === '' || typeof number !== 'number' || !Number.isFinite(number)) return null
  const hits = (byPosition.get(Math.trunc(number) + offset) ?? []).filter((uid) => rooms.get(uid)!.name === name)
  return hits.length === 1 ? hits[0] : null
}

/** The offset that lines up the most spaces; null when none does or two tie. */
function detectOffset(
  spaces: ReadonlyArray<RoomMapSpace>,
  names: string[],
  byPosition: Map<number, string[]>,
  rooms: Map<string, RoomMapRoom>,
): number | null {
  let best: number | null = null
  let bestCount = 0
  let tied = false
  for (const offset of OFFSETS) {
    let count = 0
    spaces.forEach((space, position) => {
      if (roomAt(space, names[position], offset, byPosition, rooms) !== null) count += 1
    })
    if (count > bestCount) {
      best = offset
      bestCount = count
      tied = false
    } else if (count > 0 && count === bestCount) {
      tied = true
    }
  }
  return tied ? null : best
}

/**
 * Which room each space takes: space position -> room uid. Position first
 * (with the detected offset), then a name unique on both sides; a room goes
 * to one space only.
 */
export function matchRoomsToSpaces(spaces: ReadonlyArray<RoomMapSpace>, rooms: Map<string, RoomMapRoom>): Map<number, string> {
  const names = spaces.map((space) => normalizeRoomText(scalar(space?.name)))
  const nameCounts = new Map<string, number>()
  for (const name of names) if (name !== '') nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1)

  const byPosition = new Map<number, string[]>()
  const byName = new Map<string, string[]>()
  for (const [uid, room] of rooms) {
    if (room.name === '') continue
    byName.set(room.name, [...(byName.get(room.name) ?? []), uid])
    if (room.position !== null) byPosition.set(room.position, [...(byPosition.get(room.position) ?? []), uid])
  }

  const matched = new Map<number, string>()
  const taken = new Set<string>()
  const offset = detectOffset(spaces, names, byPosition, rooms)
  if (offset !== null) {
    spaces.forEach((space, position) => {
      const uid = roomAt(space, names[position], offset, byPosition, rooms)
      if (uid !== null && !taken.has(uid)) {
        matched.set(position, uid)
        taken.add(uid)
      }
    })
  }

  spaces.forEach((_space, position) => {
    const name = names[position]
    if (matched.has(position) || name === '' || nameCounts.get(name) !== 1 || (byName.get(name) ?? []).length !== 1) return
    const uid = byName.get(name)![0]
    if (!taken.has(uid)) {
      matched.set(position, uid)
      taken.add(uid)
    }
  })

  return matched
}

/** Each matched space's room photos (gallery order) with the room's uid, keyed by space position. */
export function assignRoomPhotos(
  photos: ReadonlyArray<RoomMapPhoto | null | undefined>,
  spaces: ReadonlyArray<RoomMapSpace>,
): Map<number, { uid: string; photos: { url: string; caption: string }[] }> {
  const rooms = roomsFromPhotos(photos)
  const assigned = new Map<number, { uid: string; photos: { url: string; caption: string }[] }>()
  for (const [position, uid] of matchRoomsToSpaces(spaces, rooms)) {
    assigned.set(position, { uid, photos: rooms.get(uid)!.photos })
  }
  return assigned
}
